import { Basic } from "../basic.ts";
import { MSToolbar } from "../trend/toolbar.js";
import { MSDataZoom } from "../trend/datazoom.js";
// import { MSMiniLegend } from "../trend/minilegend.js";
import {
    exportCSVfile,
    exportFile,
    generateFileName,
    mergeDeep,
    getFormattedValue,
    unprocessScaleType,
} from "../../lib/utils.js";
import i18next from "i18next";

// отсуты побокам у графика
export const ChartOffsets = {
    top: 30,
    topWithoutTitle: 7,
    bottom: 40,
    right: 35,
    left: 10,
};
/**
 * @class Trendbase
 * @extends Basic
 * @classdesc Базовый класс для Тренда/Графика XY
 * */
export class Trendbase extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat([
            "title",
            "chartbackground",
            "backgroundcolor",
            "gridlinethickness",
            "gridfill",
            "textcolor",
            "cursorcolor",
            "cursorthickness",
            "showcursor",
            "tickmargintopx",
            "tickmarginrighty",
            "axiscolorx",
            "xaxislabel",
            "axisthicknessx",
            "axisthicknessy",
            "xformat",
            "tickcountx",
            "tickcounty",
            "tickwidthx",
            "tickwidthy",
            "tickcolorx",
            "tickcolory",
            "tickheightx",
            "tickheighty",
            "lowresolutionmode",
            "showlegend",
            "legendopacity",
            "printminilegend",
            "showhorizontalscrollbar",
            "showverticalscrollbar",
            "scrollbarborderthickness",
            "scrollbarbordercolor",
            "scrollbarbackgroundcolor",
            "scrollbarmarkerborderthickness",
            "scrollbarmarkerbordercolor",
            "scrollbarmarkerbackgroundcolor",
            "exportnameformat",
        ]);
    }

    constructor() {
        super();
        this.style.setProperty("--left", `${ChartOffsets.left}px`);
        this.style.setProperty("--top", `${ChartOffsets.top}px`);
        this.style.setProperty("--right", `${ChartOffsets.right}px`);
        // Event tab-open для выставления корректной высоты контрола
        this.addEventListener("tab-open", () => {
            this.updateHeight(this.height);
        });
    }

    /**
     * Установить составляющие тренда
     * @param {any} shadowRoot корень контрола
     */
    setParts(shadowRoot) {
        this._main = shadowRoot.querySelector("#chart-wrapper");
        this.root = shadowRoot.querySelector("#trend__container");
        this.checkComponent("ms-datazoom", MSDataZoom);
        this.checkComponent("ms-toolbar", MSToolbar);
        // this.checkComponent("ms-minilegend", MSMiniLegend);
        // this.legend = shadowRoot.querySelector("#legend");
        this.msdatazoom = shadowRoot.querySelector("#datazoom");
        this.msydatazoom = shadowRoot.querySelector("#ydatazoom");
    }
    /**
     * Создать Тренд
     * @see https://echarts.apache.org/en/option.html
     * */
    createTrend() {
        this.toolbar = this.shadowRoot.querySelector("#toolbar");
        this.chartContainer = this.shadowRoot.getElementById("chart");
        if (!this.echarts) {
            this.echarts = echarts.init(this.chartContainer, null, {
                renderer: window._globalParams.TrendRenderingType,
                ...this.calculateChartSize(),
            });
        }
        this.pens = [];
        this.createSeriesOption();
        this.createYAxisesOption();
        this.createBasePensOptions();
    }

    createBasePensOptions() {
        this.echarts.setOption({
            title: {
                textStyle: {
                    color: "#000000",
                },
                left: "center",
            },
            legend: {
                show: false,
            },
            toolbox: {
                feature: {
                    brush: {
                        show: false,
                    },
                },
            },
            grid: {
                show: true,
                backgroundColor: "#FFFFFF",
                borderWidth: 0,
                left: 20 + 25 * this.pens.length,
            },
            xAxis: {
                boundaryGap: false,
                triggerEvent: true,
                silent: false,
                offset: 0,
                axisPointer: {
                    show: true,
                    lineStyle: {
                        color: "#000000",
                        opacity: 0.5,
                        width: 1,
                    },
                    label: {
                        show: true,
                        backgroundColor: "#004E52",
                    },
                    handle: {
                        size: 15,
                        color: "#00ff11",
                    },
                },
                axisLine: {
                    lineStyle: {
                        width: 1,
                        color: "#000000",
                    },
                },
                nameTextStyle: {
                    color: "#000000",
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: "#C0C0C0",
                    },
                },
                name: "",
            },
            yAxis: this.getYAxis(),
            series: this.pens.series,
            // backgroundColor: "#FFFFFF",
            animation: false,
            silent: false,
            brush: {
                xAxisIndex: 0,
                outOfBrush: {
                    colorAlpha: 1,
                },
            },
        });
    }
    /**
     * Создать 2 datazoom-a от echarts
     * */
    createDataZoom() {
        const zoom = this.zoomallaxes
            ? Array.from(Array(this.pens.length).keys())
            : this.currentPen || 0;

        if (zoom.length === 0) {
            return;
        }

        this.echarts.setOption({
            dataZoom: [
                {
                    id: 'xdatazoom',
                    type: "inside",
                    filterMode: "none",
                    xAxisIndex: 0,
                },
                {
                    id: 'ydatazoom',
                    type: "inside",
                    filterMode: "none",
                    yAxisIndex: zoom,
                    zoomOnMouseWheel: false,
                    disabled: true,
                },
            ],
        });

        const [dataZoom, yDataZoom] = this.echarts.getModel().option.dataZoom;
        this.datazoom = dataZoom;
        this.ydatazoom = yDataZoom;
    }

    /**
     * Метод Показать легенду
     * */
    ShowLegend() {
        if (this.legend.popup.modalComponent.active) this.legend.toggle(false);
        else this.legend.toggle(true);
        this.toolbar.setToggleState(
            this.toolbar.elements["ShowLegend"],
            this.legend.popup?.modalComponent?.active
        );
    }

    updateHeight(value) {
        const intervalContainerHeght = this.intervalContainer ? this.intervalContainer.offsetHeight : 0;
        const xdatazoomHeight = this.msdatazoom ? this.msdatazoom.offsetHeight : 0;
        const toolbarHeight = this.toolbar ? this.toolbar.offsetHeight : 0;
        const height = +value - (xdatazoomHeight + intervalContainerHeght + toolbarHeight);
        if (isNaN(height) || height < 0)
            return;
        if (this.msydatazoom) {
            const msHeight = (ChartOffsets.top / height) + (ChartOffsets.bottom / +value)
            this.msydatazoom.height = `calc(100% - ${msHeight * 100}%)`
        }
        if(this.chartContainer) this.chartContainer.height = height
        if (this.echarts) this.echarts.resize({ height })
    }

    get width() {
        return super.width;
    }
    set width(value) {
        if (value === undefined || value === super.width) return
        // double click back to start position
        if (this.cache && !this.cache.width && value) this.cache.width = value
        if (!value && this.cache.width) {
            value = this.cache.width
        }
        super.width = value;
        this.chartContainer.style.width = value;
        if (this.msdatazoom)
            this.msdatazoom.width =
                +value - ChartOffsets.left - ChartOffsets.right;
        this.echarts.resize({ width: value });
        this.height = super.height;
        if (this.height) this.updateHeight(this.height);
    }
    get height() {
        return super.height;
    }
    set height(value) {
        if (value === undefined || value === super.height) return
        // double click back to start position
        if (this.cache && !this.cache.height && value) this.cache.height = value
        if (!value && this.cache.height) {
            value = this.cache.height
        }
        super.height = value;
        setTimeout(() => {
            this.updateHeight(value);
        }, 230) // таймер нужен, чтобы компонент успел обновить высоту
    }

    /**
     * Титул
     * @type {string}
     */
    get title() {
        return this.echarts.getOption().title[0].text;
    }
    set title(value) {
        this._title = value;
        const opt = {
            title: {
                text: value,
            },
            grid: {
                top: this.lowresolutionmode && !this._title.length ? ChartOffsets.topWithoutTitle : ChartOffsets.top
            }
        }
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Заливка графика
     * @type {string}
     */
    get chartbackground() {
        return this.echarts.getOption().grid[0].backgroundColor;
    }
    set chartbackground(value) {
        const opt = { grid: { backgroundColor: value } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    get backgroundcolor() {
        return this._backgroundcolor;
    }
    set backgroundcolor(value) {
        super.backgroundcolor = value;
        this._backgroundcolor = value;
    }
    /**
     * Толщина линий сетки (такое есть?)
     * @type {number}
     */
    get gridthickness() {
        return this.echarts.getOption().xAxis[0].splitLine.lineStyle.width;
    }
    set gridthickness(value) {
        const opt1 = { xAxis: { splitLine: { lineStyle: { width: +value } } } };
        const opt2 = { yAxis: { splitLine: { lineStyle: { width: +value } } } };
        this.echarts.setOption(opt1);
        this.echarts.setOption(opt2);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt1);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt2);
    }
    /**
     * Цвет линий сетки
     * @type {string}
     */
    get gridfill() {
        return this._gridfill;
    }
    set gridfill(value) {
        this._gridfill = value
        const opt = {
            xAxis: {
                splitLine: {
                    lineStyle: {
                        color: value,
                    },
                },
            },
        };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
        const ds = this.tagName == "MS-TRENDDS";
        if (!ds) {
            this.pens.forEach(pen => {
                pen.yAxis.splitLine.lineStyle.color = value;
            });
            this.echarts.setOption({ yAxis: this.getYAxis() });
            this.cacheOptions["yAxis"] = this.getYAxis();
        }
    }
    /**
     * Цвет текста (титул, подписи оси Х)
     * @type {string}
     */
    get textcolor() {
        return (
            this._textcolor || this.echarts.getOption().title[0].textStyle.color
        );
    }
    set textcolor(value) {
        this._textcolor = value;
        this.style.setProperty('--text-color', value);
        const opt1 = { title: { textStyle: { color: value } } };
        const opt2 = { xAxis: { axisLabel: { color: value } } };
        const opt3 = { xAxis: { nameTextStyle: { color: value } } };
        this.echarts.setOption(opt1);
        this.echarts.setOption(opt2);
        this.echarts.setOption(opt3);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt1);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt2);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt3);
    }
    /**
     * Цвет курсора
     * @type {string}
     */
    get cursorcolor() {
        return (
            this._cursorColor ||
            this.echarts.getOption().xAxis[0].axisPointer.lineStyle.color ||
            "#000000"
        );
    }
    set cursorcolor(value) {
        this._cursorColor = value;
        const opt = { xAxis: { axisPointer: { lineStyle: { color: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Толщина курсора
     * @type {string}
     */
    get cursorthickness() {
        return this.echarts.getOption().xAxis[0].axisPointer.lineStyle.width;
    }
    set cursorthickness(value) {
        const opt = { xAxis: { axisPointer: { lineStyle: { width: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Видимость курсора
     * @type {string}
     */
    get showcursor() {
        return this.echarts.getOption().xAxis[0].axisPointer.show;
    }
    set showcursor(value) {
        const opt = { xAxis: { axisPointer: { show: this._toBool(value) } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Верхний отступ делений X
     * @type {number}
     */
    get tickmargintopx() {
        return this.echarts.getOption().xAxis[0].axisLabel.padding;
    }
    set tickmargintopx(value) {
        const opt = { xAxis: { axisLabel: { padding: +value } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Правый отступ делений Y
     * @type {number}
     */
    get tickmarginrighty() {
        return (
            this._tickmarginrighty ||
            this.echarts.getOption().yAxis[0].axisLabel.padding
        );
    }
    set tickmarginrighty(value) {
        this._tickmarginrighty = value;
        this.pens.yaxises.forEach &&
            this.pens.yaxises.forEach(aix => {
                aix.axisLabel.padding = +value;
            });
        this.echarts.setOption({ yAxis: this.getYAxis() });
        const opt = { yAxis: { axisLabel: { padding: +value } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Цвет оси X
     * @type {string}
     */
    get axiscolorx() {
        return (
            this._axisColorX ||
            this.echarts.getOption().xAxis[0].axisLine.lineStyle.color
        );
    }
    set axiscolorx(value) {
        this._axisColorX = value;
        const opt = { xAxis: { axisLine: { lineStyle: { color: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Подпись X
     * @type {string}
     */
    get xaxislabel() {
        return this._xaxislabel || this.echarts.getOption().xAxis[0].name;
    }
    set xaxislabel(value) {
        this._xaxislabel = value;
        const opt = { xAxis: { name: value } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Формат значений X
     * @type {string}
     */
    get xformat() {
        return this._xformat;
    }
    set xformat(value) {
        this._xformat = value;
        const opt = { title: { text: this.title } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Толщина оси X
     * @type {number}
     */
    get axisthicknessx() {
        return this.echarts.getOption().xAxis[0].axisLine.lineStyle.width;
    }
    set axisthicknessx(value) {
        const opt = { xAxis: { axisLine: { lineStyle: { width: +value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Толщина оси Y
     * @type {number}
     */
    get axisthicknessy() {
        return (
            this._axisthicknessy ||
            this.echarts.getOption().yAxis[0].axisLine.lineStyle.width
        );
    }
    set axisthicknessy(value) {
        this._axisthicknessy = value;
        this.pens.yaxises.forEach &&
            this.pens.yaxises.forEach(aix => {
                aix.axisLine.lineStyle.width = +value;
            });
        const ds = this.tagName == "MS-TRENDDS";
        if (!ds) {
            this.echarts.setOption({ yAxis: this.getYAxis() });
        }
        const opt = { yAxis: { axisLine: { lineStyle: { width: +value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Количество делений X
     * @type {number}
     */
    get tickcountx() {
        return this.echarts.getOption().xAxis[0].splitNumber;
    }
    set tickcountx(value) {
        const opt = { xAxis: { splitNumber: value } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Количество делений Y
     * @type {number}
     */
    get tickcounty() {
        return this.echarts.getOption().yAxis[0].splitNumber;
    }
    set tickcounty(value) {
        const opt = { yAxis: { splitNumber: value } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Толщина делений X
     * @type {number}
     */
    get tickwidthx() {
        return this.echarts.getOption().xAxis[0].axisTick.lineStyle.width;
    }
    set tickwidthx(value) {
        const opt = { xAxis: { axisTick: { lineStyle: { width: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Толщина делений Y
     * @type {number}
     */
    get tickwidthy() {
        return (
            this._tickwidthy ||
            this.echarts.getOption().yAxis[0].axisTick.length
        );
    }
    set tickwidthy(value) {
        this._tickwidthy = value;
        this.pens.yaxises.forEach &&
            this.pens.yaxises.forEach(aix => {
                aix.axisTick.length = +value;
            });
        this.echarts.setOption({ yAxis: this.getYAxis() });
        const opt = { yAxis: { axisTick: { length: value } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Цвет делений X
     * @type {string}
     */
    get tickcolorx() {
        return (
            this._tickColorX ||
            this.echarts.getOption().xAxis[0].axisTick.lineStyle.color
        );
    }
    set tickcolorx(value) {
        this._tickColorX = value;
        const opt = { xAxis: { axisTick: { lineStyle: { color: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Цвет делений Y
     * @type {string}
     */
    get tickcolory() {
        return (
            this._tickcolory ||
            this.echarts.getOption().yAxis[0].axisTick.lineStyle.color
        );
    }
    set tickcolory(value) {
        this._tickcolory = value;
        this.pens.yaxises.forEach &&
            this.pens.yaxises.forEach(aix => {
                aix.axisTick.lineStyle.color = value;
            });
        const ds = this.tagName == "MS-TRENDDS";
        if (!ds) {
            this.echarts.setOption({ yAxis: this.getYAxis() });
        }
        const opt = { yAxis: { axisTick: { lineStyle: { color: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Длина делений X
     * @type {number}
     */
    get tickheightx() {
        return this.echarts.getOption().xAxis[0].axisTick.length;
    }
    set tickheightx(value) {
        const opt = { xAxis: { axisTick: { length: value } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Длина делений Y
     * @type {number}
     */
    get tickheighty() {
        return (
            this._tickheighty ||
            this.echarts.getOption().yAxis[0].axisTick.lineStyle.width
        );
    }
    set tickheighty(value) {
        this._tickheighty = value;
        this.pens.yaxises.forEach &&
            this.pens.yaxises.forEach(aix => {
                aix.axisTick.lineStyle.width = value;
            });
        const ds = this.tagName == "MS-TRENDDS";
        if (!ds) {
            this.echarts.setOption({ yAxis: this.getYAxis() });
        }
        const opt = { yAxis: { axisTick: { lineStyle: { width: value } } } };
        this.echarts.setOption(opt);
        this.cacheOptions = mergeDeep(this.cacheOptions, opt);
    }
    /**
     * Отображать легенду
     * @type {boolean}
     */
    get showlegend() {
        return this._showlegend;
    }
    set showlegend(value) {
        this._showlegend = value;
        // if (this._toBool(value)) {
        //     this.legend.style.display = "";
        // } else {
        //     this.legend.style.display = "none";
        // }
    }
    /**
     * Прозрачность минилегенды
     * @type {boolean}
     */
    get legendopacity() {
        return this._legendopacity;
    }
    set legendopacity(value) {
        this._legendopacity = value;
        if (this.legend) this.legend.setOpacity(value / 100)
    }
    /**
     * Минимальный вид
     * @type {boolean}
     */
    get lowresolutionmode() {
        return this._lowresolutionmode;
    }
    set lowresolutionmode(value) {
        this._lowresolutionmode = this._toBool(value);
        if (this._lowresolutionmode) {
            this.toolbar.isvisible = false;
            this.msydatazoom.isvisible = false;
            this.msdatazoom.isvisible = false;
        } else {
            this.toolbar.isvisible = true;
            this.msydatazoom.isvisible = this.showverticalscrollbar;
            this.msdatazoom.isvisible = this.showhorizontalscrollbar;
        }

        let top = this._lowresolutionmode && !this.title?.length ? ChartOffsets.topWithoutTitle : ChartOffsets.top
        if(this.yaxislabel){
            top =+ 30 // Высота подписи Y
        }

        const gridOffsets = {
            top: top,
            bottom: ChartOffsets.bottom,
            right: ChartOffsets.right,
        };

        this.echarts.setOption({
            grid: gridOffsets,
        });

        this.cacheOptions = mergeDeep(this.cacheOptions, {
            grid: gridOffsets,
        });

        this.echarts.resize(this.calculateChartSize());
    }
    /**
     * Печать минилегенды
     * @type {boolean}
     */
    get printminilegend() {
        return this._printminilegend;
    }
    set printminilegend(value) {
        const v = this._toBool(value)
        this._printminilegend = v;
    }

    /**
     * Отображение горизонтального скроллбара
     * @type {boolean}
     */
    get showhorizontalscrollbar() {
        return this._showhorizontalscrollbar;
    }

    set showhorizontalscrollbar(value) {
        this._showhorizontalscrollbar = this._toBool(value);
        this.msdatazoom.isvisible = this._showhorizontalscrollbar
        if (!this._showhorizontalscrollbar) {
            const intervalRootElem = this.shadowRoot.getElementById("timerinterval-wrap");
            if (intervalRootElem) {
                intervalRootElem.style.marginTop = 0;
            }
        }
    }

    /**
     * Отображение вертикального скроллбара
     * @type {boolean}
     */
    get showverticalscrollbar() {
        return this._showverticalscrollbar;
    }

    set showverticalscrollbar(value) {
        this._showverticalscrollbar = this._toBool(value);
        this.msydatazoom.isvisible = this._showverticalscrollbar
    }

    /**
     * Толщина рамки полосы прокрутки
     * @type {number}
     */
    get scrollbarborderthickness() {
        return this._scrollbarborderthickness;
    }

    set scrollbarborderthickness(value) {
        this._scrollbarborderthickness = value;

        this.msdatazoom.style.borderWidth = value;
        this.msydatazoom.style.borderWidth = value;
    }

    /**
     * Цвет рамки полосы прокрутки
     * @type {string}
     */
    get scrollbarbordercolor() {
        return this._scrollbarbordercolor;
    }

    set scrollbarbordercolor(value) {
        this._scrollbarbordercolor = value;

        this.msdatazoom.style.borderColor = value;
        this.msydatazoom.style.borderColor = value;
    }

    /**
     * Заливка полосы прокрутки
     * @type {string}
     */
    get scrollbarbackgroundcolor() {
        return this._scrollbarbackgroundcolor;
    }

    set scrollbarbackgroundcolor(value) {
        this._scrollbarbackgroundcolor = value;

        this.msdatazoom.backgroundcolor = value;
        this.msydatazoom.backgroundcolor = value;
    }

    /**
     * Толщина рамки маркера полосы прокрутки
     * @type {number}
     */
    get scrollbarmarkerborderthickness() {
        return this._scrollbarmarkerborderthickness;
    }

    set scrollbarmarkerborderthickness(value) {
        this._scrollbarmarkerborderthickness = value;

        this.msdatazoom.foreborderwidth = value;
        this.msydatazoom.foreborderwidth = value;
    }

    /**
     * Цвет рамки маркера полосы прокрутки
     * @type {string}
     */
    get scrollbarmarkerbordercolor() {
        return this._scrollbarmarkerbordercolor;
    }

    set scrollbarmarkerbordercolor(value) {
        this._scrollbarmarkerbordercolor = value;

        this.msdatazoom.foreborderfill = value;
        this.msydatazoom.foreborderfill = value;
    }

    /**
     * Заливка маркера полосы прокрутки
     * @type {string}
     */
    get scrollbarmarkerbackgroundcolor() {
        return this._scrollbarmarkerbackgroundcolor;
    }

    set scrollbarmarkerbackgroundcolor(value) {
        this._scrollbarmarkerbackgroundcolor = value;

        this.msdatazoom.forecolor = value;
        this.msydatazoom.forecolor = value;
    }

    // Формат имени экспорта
    get exportnameformat() {
        return (
            this._exportNameFormat || "{Name}_{yyyy}-{MM}-{dd}-{HH}_{mm}_{ss}"
        );
    }
    set exportnameformat(value) {
        this._exportNameFormat = value;
    }

    calculateChartSize() {
        const navHeight =
            this.shadowRoot.querySelector("#toolbar").offsetHeight;
        const zoomHeight =
            this.shadowRoot.querySelector("#datazoom").offsetHeight;
        const intervalMarginTop = 30;
        const timeInterval = this.intervalContainer?.offsetHeight ? this.intervalContainer.offsetHeight + intervalMarginTop : 0
        const elementSum = navHeight + zoomHeight + timeInterval;
        const height = this.attributes.height.value - elementSum;
        if (this.msydatazoom)
            this.msydatazoom.height = Math.max(height - (ChartOffsets.top + ChartOffsets.bottom), 5);
        return { width: this.attributes.width.value, height: Math.max(height, 10) };
    }

    /**
     * Сделать перо текущим
     * @param {any} index номер пера в массиве
     */
    selectPen(index) {
        if (this.currentPen === index || !this.pens.length) return;
        this.setCurrentPen(index)
    }
    /**
     * Создать оси при инициализации
     * */
    createYAxisesOption() {
        if (this.attributes.data.value === "") {
            this.pens.yaxises = {};
            return;
        } else {
            this.pens.yaxises = [];
        }
        try {
            const pens = JSON.parse(this.attributes.data.value);
            let index = 0;
            for (let penname in pens) {
                if (index >= this.pens.length) break;
                this.pens.yaxises.push(this.pens[index].yAxis);
                this.pens.yaxises[index].splitLine.lineStyle.color =
                    this.attributes.gridfill.value;
                this.pens.yaxises[index].axisLine.lineStyle.width =
                    +this.attributes.axisthicknessy.value;
                this.pens.yaxises[index].axisTick.lineStyle.width =
                    +this.attributes.tickheighty.value;
                this.pens.yaxises[index].axisTick.lineStyle.color =
                    this.attributes.tickcolory.value;
                this.pens.yaxises[index].axisTick.lineStyle.length =
                    +this.attributes.tickwidthy.value;
                this.pens.yaxises[index].nameTextStyle.color =
                    this.attributes.textcolor.value;
                index++;
            }
        } catch (e) { }
    }

    /**
     * Генерация массива значений для экспорта или табличного представления
     *
     * @returns массив значений
     * @memberof Trendbase
     */
    _generateArrayForTable() {
        let leftBorder = this.pens.leftBorder;
        let rightBorder = this.pens.rightBorder;
        let iterators = this.pens.map(x => {
            let it = x.serie.data
                .filter(el => leftBorder <= el[0] && el[0] <= rightBorder)
                .values();
            return { it, val: it.next() };
        });
        let notTheEnd = true;
        let resultTable = [[`${i18next.t('trend_table_time')}`, ...this.pens.map(pen => pen.name)]];
        while (notTheEnd) {
            let lastLine = new Array(resultTable[0].length).fill("-");
            let min = iterators.reduce((min, x) => {
                if (!x.val.done)
                    return min === undefined
                        ? x.val.value[0]
                        : Math.min(x.val.value[0], min);
                else return min;
            }, undefined);
            // Все пустые!
            if (min === undefined) {
                break;
            }
            lastLine[0] = getFormattedValue(this.xformat, new Date(min)); //присваиваем время
            notTheEnd = false;
            iterators.forEach((x, idx, arr) => {
                if (!x.val.done && x.val.value[0] == min) {
                    const rawValue = x.val.value[1];
                    const originalValue = this.pens[idx].logscale
                        ? unprocessScaleType(rawValue, true)
                        : rawValue;
                    let text = getFormattedValue(
                        this.pens[idx]._yformat,
                        originalValue
                    );
                    lastLine[idx + 1] = text;
                    x.val = { old: text, ...x.it.next() };
                    notTheEnd = true;
                } else {
                    if (x.val.old !== undefined) {
                        lastLine[idx + 1] = x.val.old;
                    }
                }
            });
            resultTable.push(lastLine);
        }
        return resultTable;
    }

    generateDataForTable(interval) {
        let leftBorder = this.pens.leftBorder;
        let rightBorder = this.pens.rightBorder;
        let iterators = this.pens.map((x, i) => {
            let it = [...(this.pens.series[i * 2 + 1]?.data ?? []), ...this.pens.series[i * 2].data]
                .filter(el => leftBorder <= el[0] && el[0] <= rightBorder && el[1] !== null)
                .sort((a, b) => a[0] - b[0])
                .values();
                return { it, val: it.next() };
            });
        let notTheEnd = true;
        let lastMin
        let resultTable = [[{ name: `${i18next.t('trend_table_time')}` }, ...this.pens.map(pen => {
            return {
                name: pen.name,
                color: pen.color
            }
        })]]
        const maxRows = 20000
        while (notTheEnd) {
            if(resultTable.length >= maxRows){
                notTheEnd = false;
                break;
            }
            let min = iterators.reduce((min, x) => {
                if (!x.val.done)
                    return min === undefined
                        ? x.val.value[0]
                        : Math.min(x.val.value[0], min);
                else return min;
            }, undefined);
            // Все пустые!
            if (min === undefined) {
                break;
            }
            if (!lastMin || min > lastMin + interval) {
                lastMin = min
                let lastLine = new Array(resultTable[0].length).fill("-");
                lastLine[0] = min; //присваиваем время
                notTheEnd = false;
                iterators.forEach((x, idx, arr) => {
                    if (!x.val.done && x.val.value[0] == min) {
                        const rawValue = x.val.value[1];
                        const originalValue = this.pens[idx].logscale
                            ? unprocessScaleType(rawValue, true)
                            : rawValue;
                        let text = getFormattedValue(
                            this.pens[idx]._yformat,
                            originalValue
                        );
                        const obj = { text, quality: x.val.value[2] };
                        lastLine[idx + 1] = obj
                        x.val = { old: obj, ...x.it.next() };
                        notTheEnd = true;
                    } else {
                        if (x.val.old !== undefined) {
                            lastLine[idx + 1] = x.val.old;
                        }
                    }
                });
                resultTable.push(lastLine);
            } else {
                let lastLine = new Array(resultTable[0].length).fill("-");
                lastLine[0] = min; //присваиваем время
                notTheEnd = false;
                iterators.forEach((x, idx, arr) => {
                    if (!x.val.done && x.val.value[0] == min) {
                        const rawValue = x.val.value[1];
                        const originalValue = this.pens[idx].logscale
                            ? unprocessScaleType(rawValue, true)
                            : rawValue;
                        let text = getFormattedValue(
                            this.pens[idx]._yformat,
                            originalValue
                        );
                        const obj = { text, quality: x.val.value[2] };
                        lastLine[idx + 1] = obj;
                        x.val = { old: obj, ...x.it.next() };
                        notTheEnd = true;
                    } else {
                        if (x.val.old !== undefined) {
                            lastLine[idx + 1] = x.val.old;
                        }
                    }
                });
            }
        }
        return resultTable;
    }

    /**
 * Метод возврата к проектному состоянию
 */
    ClearOptions() {
        this.originPath = this.getOriginPath();
        const pensData = this.pens?.map(pen => ({
            source: pen.source,
            name: pen.name,
        }));
        this._penStates = undefined;
        this.saveItemState({ penStates: undefined }, this.windowId);
        this.saveItemState({ intervalState: undefined }, this.windowId);
        this.saveItemState({ curentPenState: undefined }, this.windowId);
        this._pens = []
        this.tableTime = null;
        this.ydatazoomenable = false;
        pensData?.forEach(pen => this.removePen({ PenName: pen.source }));
        this.sources = undefined;
        this.currentPen = this.cache.currentPen;
        this.createBasePensOptions();
        this.initTrendOption();
        this.createSeriesOption();
        this.createYAxisesOption();
        this.createDataZoom();
        this.updateUnreliableLine()
        this.echarts.setOption(
            {
                title: { textStyle: { color: this.textcolor } },
                yAxis: this.getYAxis(),
                series: this.pens.series,
                grid: { backgroundColor: this.cacheOptions?.grid?.backgroundColor || "#fff" },
                xAxis: {splitLine: {lineStyle: {color: this.gridfill || "#ccc"}}}
            },
            { replaceMerge: ["series", "yAxis"] },
        );
        if (this.legend) this.legend.render();
        if (this.trendGroups) this.trendGroups.reinit();
        this.cache?.initialPens?.forEach((pen, index) => {
            const stringPenValue = this.cacheStringPenPath[index]
            this.addDataSource(
                {
                    dataSourceId: "MPLCDataSource",
                    itemId: stringPenValue ? 0 : pen.itemId,
                    path: stringPenValue ?? pen.path,
                    queueSize: 0,
                    taskId: 0,
                    timestampsToReturn: "Source",
                    type: "LREAL",
                    usesCounts: 1,
                },
                `Pens.${index}.DataSource`
            );
        });
        this.SetParameter("interval", this.intervalInitial);
        this.SetParameter("autoscroll", this.autoscrolldefault ? true : this.getAttribute('autoscroll'));
        this.clearOptionsLegend();
    }

    /**
     * Метод Экспортировать данные в CSV
     */
    DownloadCsv() {
        const fileName =
            generateFileName(
                this.exportnameformat,
                this.elname,
                this.parentElement.getAttribute("objectid")
            ) + ".csv";

        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: window.savePath
                    ? `Экспорт данных : ${this.elname} (${window.savePath}${fileName})${window.saveDeviceDesc}`
                    : `Экспорт данных : ${this.elname}`,
            };
            $pm.resolveControl(
                this,
                "TrendSave",
                this._DownloadCsv.bind(this, fileName),
                options
            );
        } else this._DownloadCsv(fileName);
    }

    _DownloadCsv(fileName) {
        const resultTable = this._generateArrayForTable();
        let res = "";
        for (const iterator of resultTable) {
            res += `${iterator.join(";")}\n`;
        }
        exportCSVfile(res, fileName, true);
    }

    exportLog(){
        const text = JSON.stringify(this.log, null, 2);
        exportFile(text, `Trend log ${new Date()}.txt`);
    }

    exportAllTrendsLog(){
        const text = JSON.stringify(window.trendLog, null, 2);
        exportFile(text, `All trends log ${new Date()}.txt`);
    }

    exportLogArchive(){
        const text = JSON.stringify(this.logArchive, null, 2);
        exportFile(text, `Trend log archive ${this.id} ${this.elname} ${new Date()}.txt`);
    }

    /**
     * Сотворить из полотна тренда картинку svg
     * @return {string} картинка
     * */
    getSvgDataUrl() {
        const parentNode = this.shadowRoot
            .querySelector("svg")
            .parentNode.cloneNode(true);
        const svg = parentNode.querySelector("svg");
        const needToPrintLegends = this._toBool(this.printminilegend);

        if (needToPrintLegends) {
            const legend = this.legend.createSVG();
            legend.setAttribute(
                "transform",
                `translate(0, ${svg.getAttribute("height")})`
            );
            svg.appendChild(legend);
        }

        svg.setAttribute(
            "height",
            +svg.getAttribute("height") +
            (needToPrintLegends ? this.pens.length * 20 : 0)
        );

        return `data:image/svg+xml;base64,${btoa(
            unescape(encodeURIComponent(svg.outerHTML))
        )}`;
    }

    getCanvasDataUrl() {
        const elem = this.shadowRoot.querySelector("canvas");
        const needToPrintLegends = this._toBool(this.printminilegend);
        const h =
            elem.height + (needToPrintLegends ? this.pens.length * 20 : 0);
        const w = elem.width;

        const newCanvas = document.createElement("canvas");
        newCanvas.width = w;
        newCanvas.height = h;

        const newCanvasContext = newCanvas.getContext("2d");
        newCanvasContext.fillStyle = "#fff";
        newCanvasContext.fillRect(0, 0, w, h);
        newCanvasContext.drawImage(elem, 0, 0);

        if (needToPrintLegends) {
            const legend = this.legend.createSVG();
            const legendsSvgImg = new Image();
            legendsSvgImg.src =
                "data:image/svg+xml;base64," +
                btoa(
                    unescape(
                        encodeURIComponent(
                            `<svg xmlns="http://www.w3.org/2000/svg"
                        >
                            ${legend.outerHTML}
                        </svg>`
                        )
                    )
                );

            return new Promise(resolve => {
                legendsSvgImg.onload = () => {
                    newCanvasContext.drawImage(legendsSvgImg, 0, elem.height);
                    resolve(newCanvas.toDataURL("image/png"));
                };
            });
        }

        return new Promise(resolve => {
            resolve(newCanvas.toDataURL("image/png"));
        });
    }

    /**
     * Метод Сохранить изображение
     */
    SaveImageAs() {
        const fileName =
            generateFileName(
                this.exportnameformat,
                this.elname,
                this.parentElement.getAttribute("objectid")
            ) + ".png";

        const isSvgRenderer = window._globalParams.TrendRenderingType === "svg";
        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: window.savePath
                    ? `${i18next.t("trend_toolbar_exportGraph")} : ${this.elname} (${window.savePath}${fileName})${window.saveDeviceDesc}`
                    : `${i18next.t("trend_toolbar_exportGraph")} : ${this.elname}`,
            };
            $pm.resolveControl(
                this,
                "TrendSave",
                isSvgRenderer
                    ? this.saveSvgImageAs.bind(this, fileName)
                    : this.saveCanvasImageAs.bind(this, fileName),
                options
            );
        } else {
            isSvgRenderer
                ? this.saveSvgImageAs(fileName)
                : this.saveCanvasImageAs(fileName);
        }
    }

    /**
     * Внутренний метод сохрания изображения
     * */
    saveSvgImageAs(fileName) {
        const svg = this.shadowRoot.querySelector("svg");
        const h =
            svg.clientHeight * 2 +
            (this._toBool(this.printminilegend) ? this.pens.length * 20 : 0);
        const w = svg.clientWidth * 2;

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;

        const context = canvas.getContext("2d");
        context.fillStyle = "#fff";
        context.fillRect(0, 0, w, h);

        const image = new Image(w, h);
        image.src = this.getSvgDataUrl();
        image.onload = () => {
            context.drawImage(image, 0, 0, w, h);
            this.saveImg(canvas.toDataURL("image/png"), fileName);
        };
    }

    async saveCanvasImageAs(fileName) {
        const dataUrl = await this.getCanvasDataUrl();
        const image = new Image();
        image.src = dataUrl;
        image.onload = () => this.saveImg(dataUrl, fileName);
    }

    saveImg(url, fileName) {
        if (window.eFs && window.savePath && window.eFs.get().existsSync(window.savePath)) {
            if (url.startsWith("data:image/png;base64,"))
                url = url.substr("data:image/png;base64,".length);
            window.eFs
                .get()
                .writeFile(
                    `${window.savePath}${fileName}`,
                    url,
                    "base64",
                    err => {
                        if (err) {
                            $ns.add({
                                type: "error",
                                time: new Date().toLocaleString(),
                                title: i18next.t("write_file_error"),
                                text: `cannot write file: ${window.savePath}${name}${window.saveDeviceDesc}`,
                            });
                        }
                    }
                );
        } else {
            const link = document.createElement("a");
            link.download = fileName;
            //link.href = this.echarts.getDataURL({ pixelRatio: 2, backgroundColor: '#fff' }).replace("image/png", "image/octet-stream");
            link.href = url;
            link.click();
        }
    }

    /**
     * Метод Печать
     */
    Print() {
        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: window.clientPrinter
                    ? `${this.elname} ${i18next.t("trendbase_print_chart_to_printer")} ${window.PrinterName}`
                    : `${this.elname} ${i18next.t("trendbase_print_chart")}`,
            };
            $pm.resolveControl(
                this,
                "TrendPrint",
                this.print.bind(this),
                options
            );
        } else this.print();
    }
    /**
     * Внутренний метод печати
     * */
    async print() {
        const dataUrl =
            window._globalParams.TrendRenderingType === "svg"
                ? this.getSvgDataUrl()
                : await this.getCanvasDataUrl();

        const windowContent = `
            <!DOCTYPE html>
            <style>
                @media print {
                    ${!(window.ipc && window.clientPrinter)
                ? `
                            @page {
                                size: A4 landscape;
                            }
                        ` : ""
            }
                    img {
                        max-width: 100%; /* нужно, чтобы изображение не выходило за пределы окна */
                    }
                }
            </style>
            <html>
                <head>
                    <title>${i18next.t("chartcolumn_print_trend")}</title>
                </head>
                <body>
                    <img src="${dataUrl}">
                </body>
            </html>
        `;
        if (window.ipc && window.clientPrinter) {
            window.ipc
                .get()
                .send(
                    "server",
                    "print",
                    `data:text/html;charset=utf8,${windowContent}`
                );
        } else {
            this._iframe = document.createElement("iframe");
            this._iframe.onload = () => {
                let printDocument =
                    this._iframe.contentWindow || this._iframe.contentDocument;
                if (printDocument.document)
                    printDocument = printDocument.document;

                requestAnimationFrame(() => {
                    this._iframe.focus();
                    this._iframe.contentWindow.print();
                    this._iframe.remove();
                    this._iframe = null;
                });
            };
            this._iframe.srcdoc = windowContent;
            document.body.appendChild(this._iframe);
        }
    }
    /**
     * Создать область границы
     * @param {number} max максимум границы
     * @param {object} penlimit данные границы
     * @return {object} граница в формате echarts
     */
    createMarkArea(max, penlimit) {
        return [
            {
                yAxis: max,
            },
            {
                name: "",
                yAxis: +penlimit.datasource,
                itemStyle: {
                    color: penlimit.backgroundcolor,
                    opacity: 0.2,
                },
            },
        ];
    }
    /**
     * Создать линию границы
     * @param {object} penlimit данные границы
     * @return {object} граница в формате echarts
     */
    createMarkLine(penlimit) {
        return {
            yAxis: +penlimit.datasource,
            lineStyle: {
                color: penlimit.color,
                width: +penlimit.thickness,
            },
        };
    }
    /**
     * Безопасно получить границы перьев из атрибута penlimits.
     * @return {object} объект границ или пустой объект
     */
    getPenlimits() {
        const rawPenlimits = this.attributes?.penlimits?.value;
        if (!rawPenlimits) {
            return {};
        }
        try {
            const parsedPenlimits = JSON.parse(rawPenlimits);
            return parsedPenlimits && typeof parsedPenlimits === "object"
                ? parsedPenlimits
                : {};
        } catch {
            return {};
        }
    }
    /**
     * Нарисовать 4 границы
     * @param {object} penlimits данные о границах
     */
    createMarkAreaOption(penlimits) {
        let markarea = [];
        let markline = [];
        // выход если границы отсутствуют
        if (penlimits) {
            if (penlimits["hihi"] && penlimits["hihi"].isvisible) {
                markarea.push(
                    this.createMarkArea(penlimits.max, penlimits["hihi"])
                );
                markline.push(this.createMarkLine(penlimits["hihi"]));
            }
            if (penlimits["hi"] && penlimits["hi"].isvisible) {
                const areas = penlimits["hihi"]
                    ? penlimits["hihi"].datasource
                    : penlimits.max;
                markarea.push(this.createMarkArea(areas, penlimits["hi"]));
                markline.push(this.createMarkLine(penlimits["hi"]));
            }
            if (penlimits["lo"] && penlimits["lo"].isvisible) {
                const areas = penlimits["lolo"]
                    ? penlimits["lolo"].datasource
                    : penlimits.min;
                markarea.push(this.createMarkArea(areas, penlimits["lo"]));
                markline.push(this.createMarkLine(penlimits["lo"]));
            }
            if (penlimits["lolo"] && penlimits["lolo"].isvisible) {
                markarea.push(
                    this.createMarkArea(penlimits.min, penlimits["lolo"])
                );
                markline.push(this.createMarkLine(penlimits["lolo"]));
            }
        }
        this.applyMark(markarea, markline);
    }

    updateEnableState(isenabled) {
        this.root.style.pointerEvents = isenabled ? "auto" : "none";
    }
}
